import React, { useEffect, useState } from "react";
import { PageHeader, Tag } from "../components/UI";
import { MessageSquare } from "lucide-react";
import api, { formatError } from "../lib/api";

export default function Grievances() {
  const [items, setItems] = useState([]);
  const [remarkDrafts, setRemarkDrafts] = useState({});

  async function fetchItems() {
    const { data } = await api.get("/tickets");
    setItems(data);
  }
  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const { data } = await api.get("/tickets");
        if (alive) setItems(data);
      } catch { /* swallow */ }
    })();
    return () => { alive = false; };
  }, []);

  async function update(id, status) {
    const remark = (remarkDrafts[id] || "").trim();
    try {
      await api.patch(`/tickets/${id}`, remark ? { status, remark } : { status });
      setRemarkDrafts((d) => { const n = { ...d }; delete n[id]; return n; });
      await fetchItems();
    } catch (e) { alert(formatError(e)); }
  }

  const cols = [
    { id: "open", label: "Open" },
    { id: "in_progress", label: "In Progress" },
    { id: "escalated", label: "Escalated" },
    { id: "resolved", label: "Resolved" },
  ];

  return (
    <div className="space-y-6">
      <PageHeader
        kicker="Operations"
        title="Grievance Desk"
        subtitle="SLA-tracked ticketing across WhatsApp inbound and admin-raised cases — with priority routing and resolution workflows. Add a remark when moving a ticket to another status."
        testId="grievances-page"
      />

      <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
        {cols.map((c) => {
          const list = items.filter((t) => t.status === c.id);
          return (
            <div key={c.id} className="bg-white border border-slate-200 rounded-lg p-3 min-h-[300px]" data-testid={`grievance-col-${c.id}`}>
              <div className="flex items-center justify-between mb-2">
                <div className="font-medium text-[13px] text-slate-900">{c.label}</div>
                <Tag tone="slate">{list.length}</Tag>
              </div>
              <div className="space-y-2">
                {list.map((t) => (
                  <div key={t.id} className="border border-slate-200 rounded-md p-3 bg-slate-50/40 hover:bg-white" data-testid={`ticket-${t.id}`}>
                    <div className="flex items-center justify-between">
                      <Tag tone={t.priority === "high" ? "rose" : t.priority === "medium" ? "amber" : "slate"}>{t.priority}</Tag>
                      <span className="text-[10px] text-slate-400">{new Date(t.created_at).toLocaleDateString()}</span>
                    </div>
                    <div className="font-medium text-[13px] text-slate-900 mt-2">{t.subject}</div>
                    <div className="text-[11px] text-slate-500 capitalize mt-1">{t.category}</div>
                    <p className="text-[11.5px] text-slate-600 mt-2 line-clamp-2">{t.description}</p>

                    {/* Remark history */}
                    {Array.isArray(t.notes) && t.notes.length > 0 && (
                      <div className="mt-2 pt-2 border-t border-slate-100 space-y-1" data-testid={`ticket-notes-${t.id}`}>
                        {t.notes.slice(-3).map((n, i) => (
                          <div key={i} className="text-[10.5px] text-slate-600 flex items-start gap-1">
                            <MessageSquare size={10} className="text-slate-400 mt-0.5 shrink-0" />
                            <span><span className="text-slate-800">{n.text}</span>{n.by ? <span className="text-slate-400"> — {n.by}</span> : null}</span>
                          </div>
                        ))}
                      </div>
                    )}

                    {/* Remark input (sent with the next status move) */}
                    <input
                      value={remarkDrafts[t.id] ?? ""}
                      onChange={(e) => setRemarkDrafts((d) => ({ ...d, [t.id]: e.target.value }))}
                      placeholder="Remark (optional)…"
                      className="!py-1 !text-[11px] w-full mt-2"
                      data-testid={`ticket-remark-input-${t.id}`}
                    />

                    <div className="flex flex-wrap items-center gap-2 mt-2">
                      {cols.filter((x) => x.id !== c.id).map((x) => (
                        <button key={x.id} onClick={() => update(t.id, x.id)} className="text-[10.5px] px-2 py-1 rounded-md border border-slate-200 hover:bg-slate-100" data-testid={`ticket-move-${t.id}-${x.id}`}>→ {x.label}</button>
                      ))}
                    </div>
                  </div>
                ))}
                {list.length === 0 && <div className="text-[11px] text-slate-400 text-center py-6">No tickets</div>}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
